import { z } from "zod";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import OptionsCell from "../components/OptionsCell/OptionsCell";
import EditableTable from "../components/EditableTable/EditableTable";
import EditableCell from "../components/EditableCell/EditableCell";
import UnsavedChangesModal from "../../../../components/UnsavedChangesModal/UnsavedChangesModal";
import { useToast } from "../../../../ui/Toast/ToastContext";
import { graphql } from "../../../../__generated__/gql";
import Loader from "../../../../ui/Loader/Loader";
import { useMutation, useQuery } from "urql";
import { useCallback, useMemo } from "react";

const TagsQuery = graphql(/* GraphQL */ `
  query tags {
    tags {
      id
      name
      uniqueId
      description
    }
  }
`);

const CreateTagsMutation = graphql(/* GraphQL */ `
  mutation createTags($input: [CreateTagInput!]!) {
    createTags(input: $input) {
      id
      name
      uniqueId
      description
    }
  }
`);

const UpdateTagsMutation = graphql(/* GraphQL */ `
  mutation updateTags($input: [UpdateTagInput!]!) {
    updateTags(input: $input) {
      id
      name
      uniqueId
      description
    }
  }
`);

const DeleteTagsMutation = graphql(/* GraphQL */ `
  mutation deleteTags($ids: [String!]!) {
    deleteTags(ids: $ids) {
      id
    }
  }
`);

type TagsTableRow = {
  id?: string;
  name?: string | null;
  uniqueId?: string | null;
  description?: string | null;
};

const columnHelper = createColumnHelper<TagsTableRow>();

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const columns: ColumnDef<TagsTableRow, any>[] = [
  columnHelper.accessor("name", {
    header: "Name",
    cell: EditableCell,
    meta: { tooltip: "The name of your article Tag or Category" },
  }),
  columnHelper.accessor("uniqueId", {
    header: "Unique ID",
    cell: EditableCell,
    meta: {
      tooltip:
        "The unique identifier of your tag within your CMS. This is used when publishing to map tags correctly.",
    },
  }),
  columnHelper.accessor("description", {
    header: "Description",
    cell: EditableCell,
    meta: {
      tooltip:
        "A description of the tag to help Macaw accurately categorize articles",
    },
  }),
  columnHelper.display({
    id: "Options",
    size: 72,
    meta: {
      sticky: true,
    },
    cell: OptionsCell,
  }),
];

const schema = z.object({
  rows: z.array(
    z.object({
      id: z.string().optional(),
      name: z.string().optional().nullable(),
      uniqueId: z.string().optional().nullable(),
      description: z.string().optional().nullable(),
    }),
  ),
});

type TagsFormData = z.infer<typeof schema>;

function TagsTable({ data }: { data: TagsTableRow[] }) {
  const [, updateTags] = useMutation(UpdateTagsMutation);
  const [, createTags] = useMutation(CreateTagsMutation);
  const [, deleteTags] = useMutation(DeleteTagsMutation);
  const { showToast } = useToast();

  const methods = useForm<TagsFormData>({
    defaultValues: {
      rows: data,
    },
    resolver: zodResolver(schema),
  });

  const onSubmit = useCallback(
    async (data: TagsFormData) => {
      const rowsWithIndex = data.rows.map((row, index) => ({ index, ...row }));
      const tagsToCreate = rowsWithIndex.filter((f) => !f.id);
      const tagsToUpdate = rowsWithIndex.filter(
        (f, index): f is typeof f & { id: string } =>
          !!(f.id && methods.getFieldState(`rows.${index}`).isDirty),
      );
      const tagsToDelete =
        methods.formState.defaultValues?.rows?.filter(
          (f): f is typeof f & { id: string } =>
            !!(f?.id && !data.rows.find((row) => row.id === f.id)),
        ) ?? [];
      const [createdTags, updatedTags] = await Promise.all([
        tagsToCreate.length
          ? createTags({
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              input: tagsToCreate.map(({ index, id, ...f }) => f),
            })
          : null,
        tagsToUpdate.length
          ? updateTags({
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              input: tagsToUpdate.map(({ index, ...f }) => f),
            })
          : null,
        tagsToDelete.length
          ? deleteTags({
              ids: tagsToDelete.map((f) => f?.id),
            })
          : null,
      ]);

      const newTags = data.rows.map((tag, index) => {
        const createdTagIndex = tagsToCreate.findIndex(
          (f) => f.index === index,
        );

        if (createdTagIndex >= 0) {
          return createdTags?.data?.createTags[createdTagIndex];
        }

        const updatedTagIndex = tagsToUpdate.findIndex(
          (f) => f.index === index,
        );

        if (updatedTagIndex >= 0) {
          return updatedTags?.data?.updateTags[updatedTagIndex];
        }

        return tag;
      });

      methods.reset({ rows: newTags.filter(Boolean) });
      showToast({
        title: "Tags saved",
        variant: "success",
      });
    },
    [createTags, deleteTags, methods, showToast, updateTags],
  );

  return (
    <FormProvider {...methods}>
      <UnsavedChangesModal isDirty={methods.formState.isDirty} />
      <EditableTable
        propertyName="rows"
        onSubmit={onSubmit}
        columns={columns}
        title="Tags"
        description="Configure your article Tags and Categories for Macaw to chose from"
        resourceName={{ plural: "tags", singular: "tag" }}
        defaultValue={{
          id: "",
          name: "",
          uniqueId: "",
          description: "",
        }}
        maxRows={45}
      />
    </FormProvider>
  );
}

function Tags() {
  const [tagsResponse] = useQuery({ query: TagsQuery });

  const data: TagsTableRow[] = useMemo(() => {
    if (!tagsResponse.data?.tags?.length) {
      return [];
    }

    return tagsResponse.data.tags.map((tag) => ({
      id: tag.id,
      name: tag.name,
      uniqueId: tag.uniqueId,
      description: tag.description,
    }));
  }, [tagsResponse]);

  if (tagsResponse.fetching) {
    return (
      <div
        style={{
          padding: "32px 0",
          width: "100%",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Loader />
      </div>
    );
  }

  return <TagsTable data={data} />;
}

export default Tags;
